import { AxiosInstance } from 'axios';
import { Person, PersonsResponse, PERSON_SCIM_ATTRIBUTES, mapScimUserToPerson } from '../types/Person';
import { extractListLevelDollarUnderscoreProps } from '../utils/listResponseMeta';
import { getScimConfig } from '../utils/envUtils';
import { ScimAuthService } from './ScimAuthService';
import { getAxiosInstance } from './axiosInstance';
import { isAbortError } from '../utils/abortUtils';
import { debugVerboseLog } from '../utils/debugLog';

/**
 * Service for efficient Person-based API calls using SCIM attribute selection
 * Reduces server load by only requesting the fields needed for table views
 */
export class ScimPersonService {
    private api: AxiosInstance;

    constructor() {
        const scimConfig = getScimConfig();
        this.api = getAxiosInstance(scimConfig.apiUrl);
        debugVerboseLog(`🔧 ScimPersonService initialized with baseURL=${scimConfig.apiUrl}`);
    }

    /**
     * Search for persons with only the essential attributes for table display
     * @param params Search parameters
     * @returns Promise<PersonsResponse> - Lightweight person data
     */
    async searchPersons(params: {
        filter?: string;
        page?: number;
        pageSize?: number;
        sortBy?: string;
        sortOrder?: 'asc' | 'desc';
        searchtext?: string;
        searchAttributes?: string;
        /** When true, omit searchdialect=expression for simple search. */
        omitSearchDialectExpression?: boolean;
        /** When provided, used for the attributes query param (e.g. All Users view without JobCode). */
        attributes?: string[];
        /** When false, allows reference attributes (e.g. Photo) to be returned. Defaults to true. */
        skipReferenceExpansion?: boolean;
    }, abortSignal?: AbortSignal): Promise<PersonsResponse> {
        try {
            const attrs = params.attributes && params.attributes.length > 0
                ? params.attributes
                : [...PERSON_SCIM_ATTRIBUTES];
            // API expects plain names (e.g. "Config"), not "$.Config" — strip "$." and dedupe.
            const sanitizedAttrs = [...new Set(
                attrs
                    .map((a) => (a ?? '').toString().trim().replace(/^\$\./, ''))
                    .filter((a) => a.length > 0)
            )];
            const queryParams: any = {
                attributes: sanitizedAttrs.join(','),
                skipReferenceExpansion: params.skipReferenceExpansion ?? true
            };

            if (params.filter) {
                queryParams.filter = params.filter;
            }

            if (params.searchtext) {
                queryParams.searchtext = params.searchtext;
                queryParams.searchdialect = params.omitSearchDialectExpression ? 'freetext' : 'expression';
            }

            // Pass through searchAttributes if provided (comma-separated list of fields to search)
            if (params.searchAttributes) {
                queryParams.searchAttributes = params.searchAttributes;
            }

            if (params.page && params.pageSize) {
                const startIndex = (params.page - 1) * params.pageSize + 1;
                queryParams.startIndex = startIndex;
                queryParams.count = params.pageSize;
            }

            if (params.sortBy) {
                // sortBy is expected in format "fieldName:direction" (e.g., "DisplayName:ascending")
                // This matches the SCIM v2 sortBy parameter format expected by the backend
                queryParams.sortBy = params.sortBy;
            }

            // Add Debug parameter if debug mode is enabled
            const { debugService } = await import('./DebugService');
            if (debugService.isEnabled()) {
                queryParams.Debug = 'true';
            }

            const response = await this.api.get('/entities/Person', {
                params: queryParams,
                signal: abortSignal
            });

            const data = response.data;

            // Map SCIM users to Person objects
            const persons: Person[] = (data.Resources || []).map(mapScimUserToPerson);

            const listLevelDollarProps = extractListLevelDollarUnderscoreProps(data);

            const result: PersonsResponse = {
                ...listLevelDollarProps,
                Resources: persons,
                totalResults: data.totalResults || 0,
                startIndex: data.startIndex || 1,
                itemsPerPage: data.itemsPerPage || persons.length
            };

            return result;

        } catch (error) {
            if (isAbortError(error, abortSignal)) {
                console.warn('⚠️ ScimPersonService: Search cancelled:', error);
            } else {
                console.error('❌ ScimPersonService: Search failed:', error);
            }
            throw ScimAuthService.handleAuthError(error);
        }
    }

    /**
     * Get a single person by ID with essential attributes only
     * @param id Person ID
     * @returns Promise<Person | null>
     */
    async getPersonById(id: string): Promise<Person | null> {
        try {
            const queryParams = {
                attributes: PERSON_SCIM_ATTRIBUTES.join(',')
            };

            const response = await this.api.get(`/entities/Person/${id}`, {
                params: queryParams
            });

            const data = response.data;

            return mapScimUserToPerson(data);

        } catch (error: any) {
            if (error.response?.status === 404) {
                return null;
            }
            if (isAbortError(error)) {
                console.warn(`⚠️ ScimPersonService: Get person ${id} cancelled:`, error);
            } else {
                console.error(`❌ ScimPersonService: Failed to get person ${id}:`, error);
            }
            throw ScimAuthService.handleAuthError(error);
        }
    }
}

// Export singleton instance
export const scimPersonService = new ScimPersonService();
